import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DownloadSimple, PaperPlaneTilt, Eye, FileText } from "@phosphor-icons/react";
import { SendChecklistDialog } from "@/components/SendChecklistDialog";
import { toast } from "sonner";

export default function ReportHistory() {
  const [reports, setReports] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    const { data } = await api.get("/reports");
    setReports(data);
  };
  useEffect(() => { load(); }, []);

  const download = async (r) => {
    try {
      const res = await api.get(`/reports/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `report_${r.report_date}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const doSend = async () => {
    if (!sendTarget) return;
    setSending(true);
    try {
      const { data } = await api.post(`/reports/${sendTarget.id}/send-email`);
      toast.success(`${data.message}${data.mocked ? " (mock)" : ""}`);
      setSendTarget(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">Archive</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-normal tracking-tight gradient-text mt-1">Report History</h1>
      </div>

      {reports && reports.length === 0 && (
        <Card className="p-12 rounded-md border-border border-dashed text-center">
          <FileText size={40} className="mx-auto text-muted-foreground" />
          <p className="mt-3 text-muted-foreground">No reports yet. Submit your first daily report.</p>
        </Card>
      )}

      {reports && reports.length > 0 && (
        <Card className="rounded-md border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="font-medium">Date</TableHead>
                <TableHead className="font-medium">Summary</TableHead>
                <TableHead className="font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((r) => (
                <TableRow key={r.id} data-testid={`report-row-${r.id}`} className="hover:bg-secondary/30 transition-colors duration-200">
                  <TableCell className="font-mono-num text-sm whitespace-nowrap">{r.report_date}</TableCell>
                  <TableCell className="max-w-md truncate text-muted-foreground">{r.summary}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" data-testid={`view-report-${r.id}`} onClick={() => setSelected(r)} title="View"><Eye size={18} /></Button>
                      <Button size="icon" variant="ghost" data-testid={`download-report-${r.id}`} onClick={() => download(r)} title="Download PDF"><DownloadSimple size={18} /></Button>
                      <Button size="icon" variant="ghost" data-testid={`email-report-${r.id}`} onClick={() => setSendTarget(r)} title="Send to email"><PaperPlaneTilt size={18} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Report · {selected?.report_date}</DialogTitle>
            <DialogDescription>Full details of your submitted daily work report.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Summary</p>
                <p className="mt-1 text-sm">{selected.summary}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {selected.hour_logs?.length ? "Hourly Log" : "Tasks"}
                </p>
                {selected.hour_logs?.length ? (
                  <div className="mt-2 rounded-md border border-border overflow-hidden divide-y divide-border">
                    {selected.hour_logs.map((h, i) => (
                      <div key={i} className="flex text-sm">
                        <span className="w-24 shrink-0 px-3 py-2 font-mono-num text-xs font-medium bg-secondary/60 text-primary border-r border-border">{h.hour}</span>
                        <div className="flex-1 px-3 py-2 space-y-1">
                          <p>{h.text || "—"}</p>
                          {h.challenges && <p className="text-xs text-amber-700"><span className="font-medium">Challenges:</span> {h.challenges}</p>}
                          {h.suggestions && <p className="text-xs text-sky-700"><span className="font-medium">Suggestions:</span> {h.suggestions}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-sm whitespace-pre-wrap leading-relaxed">{selected.tasks}</p>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => download(selected)} className="rounded-md bg-primary hover:bg-primary/90"><DownloadSimple size={16} className="mr-1.5" /> PDF</Button>
                <Button size="sm" variant="outline" onClick={() => setSendTarget(selected)} className="rounded-md"><PaperPlaneTilt size={16} className="mr-1.5" /> Email</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SendChecklistDialog open={!!sendTarget} onOpenChange={(o) => !o && setSendTarget(null)} onConfirm={doSend} sending={sending} />
    </div>
  );
}
